import React, { useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { Link } from 'react-router-dom';

// Parse "Mar 30, 2026 04:04 PM" format and extract date part
const getDateFromModifiedAt = (modifiedAt) => {
    if (!modifiedAt) return 'Unknown';
    // Extract just the date part: "Mar 30, 2026"
    const parts = modifiedAt.split(' ');
    if (parts.length >= 3) {
        return `${parts[0]} ${parts[1]} ${parts[2]}`;
    }
    return modifiedAt;
};

// Parse "Mar 30, 2026 04:04 PM" format and extract time part
const getTimeFromModifiedAt = (modifiedAt) => {
    if (!modifiedAt) return '';
    // Extract time part: "04:04 PM"
    const parts = modifiedAt.split(' ');
    if (parts.length >= 5) {
        return `${parts[3]} ${parts[4]}`;
    }
    return '';
};

const groupMessagesByDate = (messages) => {
    if (!Array.isArray(messages)) return {};
    return messages.reduce((groups, message) => {
        const date = getDateFromModifiedAt(message.modifiedAt);
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(message);
        return groups;
    }, {});
};

// Format time - handles both "04:04 PM" format and legacy "HH:MM:SS" format
function formatTime(modifiedAt) {
    if (!modifiedAt) return '';
    // If it's the new format "Mar 30, 2026 04:04 PM", extract time
    const timeStr = getTimeFromModifiedAt(modifiedAt);
    if (timeStr) return timeStr;
    // Fallback for legacy format
    return modifiedAt;
}

function formatDateLabel(dateString) {
    if (!dateString || dateString === 'Unknown') return dateString;
    
    // Parse "Mar 30, 2026" format
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
        return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
    } else {
        return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
}

const ConversationList = ({ smsData, handleSendMessage, getConversationDetails, conversationDetailsLoading, conversationDetails }) => {
    const groupedMessages = groupMessagesByDate(smsData);
    const dates = Object.keys(groupedMessages);

    const [charCount, setCharCount] = useState(0);
    const [message, setMessage] = useState('');
    const messagesEndRef = useRef(null);
    const [maxCount, setMaxCount] = useState(false);
    const [show, setShow] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => {
        getConversationDetails();
        setShow(true);
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleInputChange = (event) => {
        const inputValue = event.target.value;
        if (inputValue.length <= 450) {
            setMessage(inputValue);
            setCharCount(inputValue.length);
            setMaxCount(false);
        } else {
            setMaxCount(true);
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [smsData]);

    const sendMessage = () => {
        if (message.trim() !== '') {
            const confirmationRequired = document.getElementById('confirmationrequired').checked;
            handleSendMessage(message, confirmationRequired);
            setMessage('');
            setCharCount(0);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const TextWithLinks = ({ text }) => {
        const urlPattern = /(https?:\/\/[^\s]+)/g;
        const parts = text.split(urlPattern).map((part, index) => {
            if (urlPattern.test(part)) {
                return (
                    <Link
                        key={index}
                        to="#"
                        className="text-primary text-decoration-underline"
                        onClick={(e) => {
                            e.preventDefault();
                            window.open(part, '_blank');
                        }}
                    >
                        {part}
                    </Link>
                );
            }
            return part;
        });
        return <>{parts}</>;
    };

    const getInitials = (firstName, lastName) => {
        const first = firstName?.charAt(0)?.toUpperCase() || '';
        const last = lastName?.charAt(0)?.toUpperCase() || '';
        return first + last || '?';
    };

    return (
        <div className="card shadow-sm border-0 d-flex flex-column" style={{ height: 'calc(100vh - 200px)' }}>
            {/* Chat Header */}
            <div className="card-header bg-white border-bottom py-3" style={{ flexShrink: 0 }}>
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <div 
                            className="d-flex align-items-center justify-content-center rounded-circle me-3"
                            style={{ 
                                width: '45px', 
                                height: '45px',
                                backgroundColor: '#e8f4fd'
                            }}
                        >
                            <span className="fw-bold" style={{ color: '#0d6efd' }}>
                                {getInitials(smsData[0].customerFirstName, smsData[0].customerLastName)}
                            </span>
                        </div>
                        <div>
                            <h6 className="mb-0 fw-bold" style={{ color: '#212529' }}>
                                {smsData[0].customerFirstName} {smsData[0].customerLastName}
                            </h6>
                            {smsData[0].customerDob && (
                                <small className="text-muted">
                                    <i className="bi bi-calendar3 me-1"></i>
                                    {smsData[0].customerDob}
                                </small>
                            )}
                        </div>
                    </div>
                    <button 
                        className="btn btn-light btn-sm rounded-circle" 
                        onClick={handleShow}
                        title="View details"
                        style={{ width: '36px', height: '36px' }}
                    >
                        <i className="bi bi-three-dots-vertical"></i>
                    </button>
                </div>
            </div>

            {/* Messages Area */}
            <div 
                className="card-body message-area" 
                style={{ 
                    overflowY: 'auto', 
                    flex: '1 1 auto',
                    minHeight: 0,
                    backgroundColor: '#f8f9fa'
                }}
            >
                <div className="d-flex flex-column">
                    {dates.map(date => (
                        <div key={date}>
                            {/* Date Separator */}
                            <div className="d-flex justify-content-center my-3">
                                <span className="badge bg-white text-muted border px-3 py-2 rounded-pill shadow-sm">
                                    {formatDateLabel(date)}
                                </span>
                            </div>

                            {/* Messages */}
                            {groupedMessages[date].map((msg, index) => (
                                <div
                                    key={index}
                                    className={`d-flex mb-3 ${msg.messageType === 'OUTGOING' ? 'justify-content-end' : 'justify-content-start'}`}
                                >
                                    <div
                                        className={`px-3 py-2 rounded-3 position-relative ${
                                            msg.messageType === 'OUTGOING' 
                                                ? 'bg-primary text-white' 
                                                : 'bg-white border shadow-sm'
                                        }`}
                                        style={{ maxWidth: '70%', minWidth: '80px' }}
                                    >
                                        <div className={`mb-1 ${msg.messageType === 'OUTGOING' ? 'text-white' : 'text-dark'}`}>
                                            <TextWithLinks text={msg.message} />
                                        </div>
                                        <div className={`d-flex align-items-center justify-content-end gap-1 ${
                                            msg.messageType === 'OUTGOING' ? 'text-white-50' : 'text-muted'
                                        }`} style={{ fontSize: '0.7rem' }}>
                                            <span>{formatTime(msg.modifiedAt)}</span>
                                            {msg.messageType === 'OUTGOING' && (
                                                msg.status === "DELIVERED" 
                                                    ? <i className="bi bi-check2-all"></i>
                                                    : msg.status === "ACCEPTED"
                                                        ? <i className="bi bi-check2"></i>
                                                        : <i className="bi bi-exclamation-circle"></i>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                <div ref={messagesEndRef} />
            </div>

            {/* Message Input Area */}
            <div className="card-footer bg-white border-top p-3" style={{ flexShrink: 0 }}>
                {maxCount && (
                    <div className="alert alert-warning py-2 px-3 mb-2 small">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        Maximum 450 characters allowed
                    </div>
                )}
                <div className="d-flex gap-2">
                    <div className="flex-grow-1">
                        <textarea 
                            className="form-control border-0 bg-light" 
                            rows="2" 
                            value={message} 
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            placeholder="Type your message..."
                            style={{ resize: 'none' }}
                        />
                    </div>
                    <button 
                        className="btn btn-primary align-self-end px-4" 
                        onClick={sendMessage}
                        disabled={!message.trim()}
                    >
                        <i className="bi bi-send-fill"></i>
                    </button>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-2">
                    <div className="form-check mb-0">
                        <input 
                            type="checkbox" 
                            name="confirmationrequired" 
                            id="confirmationrequired" 
                            className="form-check-input" 
                        />
                        <label className="form-check-label small text-muted" htmlFor="confirmationrequired">
                            Confirmation Required
                        </label>
                    </div>
                    <small className={`${charCount > 400 ? 'text-warning' : 'text-muted'}`}>
                        {charCount}/450
                    </small>
                </div>
            </div>

            {/* Details Modal */}
            <Modal show={show} onHide={handleClose} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="h5">
                        <i className="bi bi-person-lines-fill me-2 text-primary"></i>
                        Contact Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    {conversationDetailsLoading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="list-group list-group-flush">
                            {conversationDetails && conversationDetails.map((detail, index) => (
                                <div key={index} className="list-group-item px-0 py-3 border-bottom">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <h6 className="mb-1 fw-semibold">
                                                {detail.firstName} {detail.lastName}
                                            </h6>
                                            <small className="text-muted">
                                                <i className="bi bi-hash me-1"></i>
                                                {detail.customerId}
                                            </small>
                                        </div>
                                        {detail.customerDob && (
                                            <span className="badge bg-light text-muted">
                                                <i className="bi bi-calendar3 me-1"></i>
                                                {detail.customerDob}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="light" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .message-area::-webkit-scrollbar {
                    width: 6px;
                }
                .message-area::-webkit-scrollbar-track {
                    background: transparent;
                }
                .message-area::-webkit-scrollbar-thumb {
                    background: #dee2e6;
                    border-radius: 6px;
                }
                .message-area::-webkit-scrollbar-thumb:hover {
                    background: #adb5bd;
                }
            `}</style>
        </div>
    );
};

export default ConversationList;
